import React, { useState, useEffect } from "react";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Link from "@material-ui/core/Link";
import Logo from "../../Images/logo.png";
import useStyles from "./style";
import http from "../Common/RestAPIHandler";
import { triggerThemeUpdate } from "../Common/Utility";
import { connect } from "react-redux";
import TextField from "@material-ui/core/TextField";
import SnackbarComponent from "../Common/Snackbar";
import { Button } from "@material-ui/core";
import { setUser } from "../../redux-tools/actions/userActions";
import auth from "../../services/authService";


import Checkbox from "@material-ui/core/Checkbox";

const TwoFA = (props) => {
	const classes = useStyles();
	const [isInvalidToken, setIsInvalidToken] = useState(false);
	const [isSMS, setIsSMS] = useState(false);
	const [isEmail, setIsEmail] = useState(false);
	const [snackBarMessage, setSnackBarMessage] = useState();
	const [errorMessage, setErrorMessage] = useState();
	const [tempUserData, setTempUserData] = useState(null);
	const [rememberFor30Days, setRememberFor30Days] = useState(false);
	const [verificationCode, setVerificationCode] = useState("");
	const [tokenSent, setTokenSent] = useState(false);
	const [selectedMethod, setSelectedMethod] = useState(null);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const handleSms = () => {
		setIsSubmitting(true);
		setErrorMessage(null);

		http.Post(
			"auth/2fa/sms/send",
			{},
			(data) => {
				setSnackBarMessage(data.detail);
				setTokenSent(true);
				setSelectedMethod('sms');
				setIsSubmitting(false);
			},
			(status, error) => {
				setErrorMessage(error?.detail || "Failed to send SMS token");
				setIsSubmitting(false);
			}
		);
	};

	const handleEmail = () => {
		setIsSubmitting(true);
		setErrorMessage(null);

		http.Post(
			"auth/2fa/email/send",
			{},
			(data) => {
				setSnackBarMessage(data.detail);
				setTokenSent(true);
				setSelectedMethod('email');
				setIsSubmitting(false);
			},
			(status, error) => {
				setErrorMessage(error?.detail || "Failed to send email token");
				setIsSubmitting(false);
			}
		);
	};

	const handleTokenSubmit = (token) => {
			if (!token || token.length !== 7) {
				setIsInvalidToken(true);
				return;
			}

			if (!selectedMethod) {
				setErrorMessage("Please request a verification code first");
				return;
			}

			setIsSubmitting(true);
			setIsInvalidToken(false);
			setErrorMessage(null);

			const payload = {
				token: token,
				type: selectedMethod,
				dont_show_30: rememberFor30Days
			};

			http.Post(
				"auth/2fa/verify",
				payload,
				(response) => {
					// Validate that we have complete user data
					if (!tempUserData || !tempUserData.user_type) {
						setErrorMessage("Session expired. Please login again.");
						setIsSubmitting(false);
						return;
					}

					// *** NO TOKEN UPDATE NEEDED - Original tokens still valid ***
					console.log("2FA Frontend: 2FA completed, using original tokens");

					// Update login_authorized status in temp data for consistency
					tempUserData.login_authorized = response.login_authorized;

					// Update sessionStorage with updated status
					sessionStorage.setItem('temp_user_data', JSON.stringify(tempUserData));

					// For tenant users, ensure we have company_id
					if (tempUserData.user_type === "Tenant User" && !tempUserData.company_id) {
						tempUserData.company_id = tempUserData.tenant;
					}

					// Ensure date formats are set
					if (!tempUserData.date_format) {
						tempUserData.date_format = "DD/MM/YYYY";
					}
					if (!tempUserData.date_time_format) {
						tempUserData.date_time_format = "DD/MM/YYYY HH:mm";
					}

					// Show success message
					if (response.dont_show_30) {
						setSnackBarMessage("2FA verification successful! You won't be asked for 2FA again for 30 days.");
					} else {
						setSnackBarMessage("2FA verification successful!");
					}

					// Set user data and navigate
					setTimeout(() => {
						props.setUser(tempUserData);
						// Clean up temporary storage
						sessionStorage.removeItem('temp_user_data');

						// Update theme immediately
						triggerThemeUpdate();

						props.history.push(process.env.PUBLIC_URL + "/dashboard");
					}, 1500); // Give time to show the success message
				},
				(status, error) => {
					setIsInvalidToken(true);
					setErrorMessage(error?.detail || "Invalid or expired verification code");
					setIsSubmitting(false);
				}
			);
	};


	const handleVerificationCodeChange = (e) => {
		const value = e.target.value;
		setVerificationCode(value);
		setIsInvalidToken(false);

		// Auto-submit when 7 digits are entered
		if (value && value.length === 7) {
			handleTokenSubmit(value);
		}
	};

	useEffect(() => {
		// Get temporary user data stored by PasswordLogin
		const storedUserData = sessionStorage.getItem('temp_user_data');
		if (storedUserData) {
			const userData = JSON.parse(storedUserData);
			setTempUserData(userData);

			// Set 2FA options based on user data
			setIsSMS(userData.is_sms);
			setIsEmail(userData.is_email);

			// If no 2FA methods are enabled, redirect to dashboard immediately
			if (!userData.is_sms && !userData.is_email) {
				// Set user data and navigate to dashboard
				props.setUser(userData);
				// Clean up temporary storage
				sessionStorage.removeItem('temp_user_data');
				// Update theme immediately
				triggerThemeUpdate();
				props.history.replace(process.env.PUBLIC_URL + "/dashboard");
				return;
			}
		} else {
			props.history.push(process.env.PUBLIC_URL + "/login");
		}
	}, [props.setUser, props.history]);

	return (
		<div className={classes.main}>
			<div className={classes.root}>
				<SnackbarComponent
					message={snackBarMessage}
					open={!!snackBarMessage}
					handleSnackBarClose={() => {
						setSnackBarMessage(null);
					}}
					severity={"success"}
				/>
				{errorMessage && (
					<SnackbarComponent
						message={errorMessage}
						open={!!errorMessage}
						handleSnackBarClose={() => {
							setErrorMessage(null);
						}}
						severity={"error"}
					/>
				)}
				<Grid
					container
					spacing={0}
					alignItems="center"
					justify="center"
					className={classes.container}
				>
					<Grid item xs={12}>
						<Grid container spacing={3}>
							<Grid item xs={12} className={classes.centerElement}>
								<Typography variant="h6" color="inherit" noWrap>
									<img
										src={Logo}
										alt="Not Available"
										className={classes.logo}
									/>
								</Typography>
							</Grid>

							<Grid item xs={12}>
								<Typography variant="h5" align="center" gutterBottom className={classes.title}>
									Two-Factor Authentication
								</Typography>
								<Typography variant="body1" align="center" className={classes.title}>
									Please verify your identity to continue
								</Typography>
							</Grid>

							{/* Show error message if no 2FA methods are available */}
							{!isSMS && !isEmail && (
								<Grid item xs={12}>
									<Typography variant="body1" align="center" className={classes.title} style={{color: 'red'}}>
										No 2FA methods are configured for your account. Redirecting to dashboard...
									</Typography>
								</Grid>
							)}

							{/* 2FA Method Selection */}
							{(isSMS || isEmail) && !tokenSent && (
								<Grid item xs={12}>
									<Typography variant="h6" gutterBottom className={classes.title}>
										Choose verification method:
									</Typography>
									<Grid container spacing={2}>
										{isSMS && (
											<Grid item xs={isEmail ? 6 : 12}>
												<Button
													className={classes.button}
													variant="outlined"
													fullWidth
													onClick={handleSms}
													disabled={isSubmitting}
												>
													{isSubmitting && selectedMethod === null ? 'Sending...' : 'SMS Verification'}
												</Button>
											</Grid>
										)}
										{isEmail && (
											<Grid item xs={isSMS ? 6 : 12}>
												<Button
													className={classes.button}
													variant="outlined"
													fullWidth
													onClick={handleEmail}
													disabled={isSubmitting}
												>
													{isSubmitting && selectedMethod === null ? 'Sending...' : 'Email Verification'}
												</Button>
											</Grid>
										)}
									</Grid>
								</Grid>
							)}

							{/* Verification Code Input */}
							{tokenSent && (
								<>
									<Grid item xs={12}>
										<Typography variant="h6" gutterBottom className={classes.title}>
											Enter verification code:
										</Typography>
										<TextField
											id="verification-code-input"
											label="Verification Code"
											type="text"
											margin="dense"
											variant="outlined"
											fullWidth
											value={verificationCode}
											onChange={handleVerificationCodeChange}
											disabled={isSubmitting}
											inputProps={{
												maxLength: 7,
												pattern: "[0-9]*"
											}}
											error={isInvalidToken}
											helperText={isInvalidToken ? "Invalid or expired verification code" :
														`Enter the 7-digit code sent via ${selectedMethod === 'sms' ? 'SMS' : 'Email'}`}
											FormHelperTextProps={{
												className: isInvalidToken ? classes.error : classes.title
											}}
										/>
									</Grid>

									{/* Remember for 30 Days Option */}
									<Grid item xs={12}>
										<Grid container spacing={1} alignItems="center">
											<Grid item>
												<Checkbox
													checked={rememberFor30Days}
													onChange={(e) => setRememberFor30Days(e.target.checked)}
													className={classes.checkbox}
												/>
											</Grid>
											<Grid item>
												<Typography variant="body2" className={classes.title}>
													Don't show again for 30 days
												</Typography>
											</Grid>
										</Grid>
									</Grid>

									{/* Resend Options */}
									<Grid item xs={12}>
										<Typography variant="body2" align="center" gutterBottom className={classes.title}>
											Didn't receive the code?
										</Typography>
										<Grid container spacing={2}>
											{selectedMethod === 'sms' && (
												<Grid item xs={12}>
													<Button
														className={classes.button}
														variant="text"
														fullWidth
														onClick={() => {
															setTokenSent(false);
															setSelectedMethod(null);
															setVerificationCode("");
															handleSms();
														}}
														disabled={isSubmitting}
													>
														Resend SMS
													</Button>
												</Grid>
											)}
											{selectedMethod === 'email' && (
												<Grid item xs={12}>
													<Button
														className={classes.button}
														variant="text"
														fullWidth
														onClick={() => {
															setTokenSent(false);
															setSelectedMethod(null);
															setVerificationCode("");
															handleEmail();
														}}
														disabled={isSubmitting}
													>
														Resend Email
													</Button>
												</Grid>
											)}
										</Grid>
									</Grid>


								</>
							)}

							<Grid item xs={12} className={classes.centerElement}>
								<Link href="/login" className={classes.link}>Return to login page</Link>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</div>
		</div>
	);
};

export default connect(null, { setUser })(TwoFA);
